import { useCreatePartner, useUpdatePartner } from '@/api/partner-data';
import useConsult from '@/hooks/useConsult';
import useForm from '@/hooks/useForm';
import useSubmitGuard from '@/hooks/useSubmitGuard';
import { isValidDate, toDateInputValue } from '@/utils/constants';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import add_user_main from '../../assets/icons/add_user_main.svg';

type FormStatus = {
  displayValidations: boolean;
  isValid: boolean;
  validationMsgs: Record<string, string>
};

const FORM_STATUS_INITIAL_STATE: FormStatus = {
  displayValidations: false,
  isValid: false,
  validationMsgs: {},
};

type PartnerFormProps = {
  activeConsultant: Api.Consultant;
  setIsAddFormActive: (isActive: boolean) => void;
  isEditing?: boolean;
  partnerToEdit?: Api.Partner;
};

export default function PartnerForm({
  activeConsultant,
  setIsAddFormActive,
  isEditing,
  partnerToEdit,
}: PartnerFormProps): JSX.Element {
  const { handleIsEditingConsultant, updateConsultantPartners, activePartnerData } = useConsult();
  const createPartnerMutation = useCreatePartner();
  const updatePartnerMutation = useUpdatePartner();

  const [isLoading, setIsLoading] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>(FORM_STATUS_INITIAL_STATE);
  const runOnce = useSubmitGuard();

  const { t } = useTranslation();

  const initialForm = {
    names: isEditing && partnerToEdit ? partnerToEdit.names : '',
    lastName: isEditing && partnerToEdit ? partnerToEdit.lastName : '',
    scdLastName: isEditing && partnerToEdit ? partnerToEdit.scdLastName : '',
    date: isEditing && partnerToEdit ? toDateInputValue(partnerToEdit.date) : '',
  };

  const {
    names,
    lastName,
    scdLastName,
    date,
    handleInputChange,
    setFormError,
    reset,
    updateValues,
  } = useForm(initialForm);

  const isFormValid = () => {
    let isValid = true;
    let validationMsgs: Record<string, string> = {};

    const letters = /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s]+$/;

    if (!names) {
      validationMsgs = { ...validationMsgs, names: t('validation.required') };
      isValid = false;
    } else if (!names.match(letters)) {
      validationMsgs = { ...validationMsgs, names: t('validation.invalid') };
      isValid = false;
    }

    if (!lastName) {
      validationMsgs = { ...validationMsgs, lastName: t('validation.required') };
      isValid = false;
    } else if (!lastName.match(letters)) {
      validationMsgs = { ...validationMsgs, lastName: t('validation.invalid') };
      isValid = false;
    }

    if (scdLastName && !scdLastName.match(letters)) {
      validationMsgs = { ...validationMsgs, scdLastName: t('validation.invalid') };
      isValid = false;
    }

    if (!date) {
      validationMsgs = { ...validationMsgs, date: t('validation.required') };
      isValid = false;
    } else if (!isValidDate(date)) {
      validationMsgs = { ...validationMsgs, date: t('validation.invalid') };
      isValid = false;
    }

    setFormStatus((prevState) => ({ ...prevState, isValid, validationMsgs }));
  };

  useEffect(() => {
    isFormValid();
  }, [names, lastName, scdLastName, date]);

  useEffect(() => {
    if (isEditing && partnerToEdit) {
      updateValues({
        names: partnerToEdit.names || '',
        lastName: partnerToEdit.lastName || '',
        scdLastName: partnerToEdit.scdLastName || '',
        date: toDateInputValue(partnerToEdit.date),
      });
    }
  }, [isEditing, partnerToEdit]);

  const closeForm = () => {
    setIsAddFormActive(false);
    handleIsEditingConsultant(false);
    reset();
  };

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formStatus.isValid) {
      setFormStatus((prevState) => ({ ...prevState, displayValidations: true }));
      return;
    }

    runOnce(async () => {
      setFormError('');
      setIsLoading(true);

      try {
        const payload = {
          names: (names || '').trim(),
          lastName: (lastName || '').trim(),
          scdLastName: (scdLastName || '').trim(),
          date: date?.toString() || '',
        };

        if (activePartnerData) {
          const savedPartner = isEditing && partnerToEdit
            ? await updatePartnerMutation.mutateAsync({
              partnerId: partnerToEdit.id,
              partner: payload,
            })
            : await createPartnerMutation.mutateAsync({
              partnerDataId: activePartnerData.id,
              partner: payload,
            });
          const updatedPartnerData: Api.PartnerData = {
            ...activePartnerData,
            partner: isEditing && partnerToEdit
              ? activePartnerData.partner?.map((p: Api.Partner) => (p.id === partnerToEdit.id ? savedPartner : p)) || []
              : [...(activePartnerData.partner || []), savedPartner],
          };

          const updatedConsultant: Api.Consultant = {
            ...activeConsultant,
            partnerData: activeConsultant.partnerData?.map((p: Api.PartnerData) => (p.id === activePartnerData.id ? updatedPartnerData : p)) || [],
          };

          // Actualizar inmediatamente el contexto con el consultor actualizado
          updateConsultantPartners(updatedConsultant);
        } else {
          throw new Error('No active partner data selected');
        }

        closeForm();
      } catch (err) {
        setFormError(err instanceof Error ? err.message : t('errors.savePartner') as string);
      } finally {
        setIsLoading(false);
      }
    });
  };
  const handleUseConsultant = () => {
    updateValues({
      names: activeConsultant.names || '',
      lastName: activeConsultant.lastName || '',
      scdLastName: activeConsultant.scdLastName || '',
      date: toDateInputValue(activeConsultant.date),
    });
  };

  return (
    <form className="block w-full mt-3" onSubmit={handleOnSubmit}>
      <h2 className="flex justify-center items-center text-xl font-bold">
        <img src={add_user_main} className="mr-3" alt="add_user_main" />
        {isEditing ? t('modal.partner.editPartner') : t('modal.partner.assignPartner')}
      </h2>

      <div className="flex w-full mt-6">
        <div className="form-group w-1/3">
          <p className="font-bold mb-1">
            {t('forms.names')}
            <span className="text-red-800">*</span>
          </p>
          <input
            id="partner-names"
            type="text"
            name="names"
            className="rounded border-[#C4C4C4] border w-11/12"
            onChange={(e) => handleInputChange(e.target)}
            value={names || ''}
          />
          {(formStatus?.displayValidations && formStatus?.validationMsgs?.names) && (
            <span className="form-error">{formStatus.validationMsgs.names}</span>
          )}
        </div>

        <div className="form-group w-1/3">
          <p className="font-bold mb-1">
            {t('forms.paternalSurname')}
            <span className="text-red-800">*</span>
          </p>
          <input
            id="partner-lastName"
            type="text"
            name="lastName"
            className="rounded border-[#C4C4C4] border w-11/12"
            onChange={(e) => handleInputChange(e.target)}
            value={lastName || ''}
          />
          {(formStatus?.displayValidations && formStatus?.validationMsgs?.lastName) && (
            <span className="form-error">{formStatus.validationMsgs.lastName}</span>
          )}
        </div>

        <div className="form-group w-1/3">
          <p className="font-bold mb-1">
            {t('forms.maternalSurname')}
          </p>
          <input
            id="partner-scdLastName"
            type="text"
            name="scdLastName"
            className="rounded border-[#C4C4C4] border w-11/12"
            onChange={(e) => handleInputChange(e.target)}
            value={scdLastName || ''}
          />
          {(formStatus?.displayValidations && formStatus?.validationMsgs?.scdLastName) && (
            <span className="form-error">{formStatus.validationMsgs.scdLastName}</span>
          )}
        </div>
      </div>

      <div className="flex w-full mt-3 items-center">
        <div className="form-group w-1/2">
          <p className="font-bold mb-1">
            {t('forms.birthDate')}
            <span className="text-red-800">*</span>
          </p>
          <input
            id="partner-date"
            type="date"
            name="date"
            className="rounded border-[#C4C4C4] border w-11/12"
            onChange={(e) => handleInputChange(e.target)}
            value={date || ''}
          />
          {(formStatus?.displayValidations && formStatus?.validationMsgs?.date) && (
            <span className="form-error">{formStatus.validationMsgs.date}</span>
          )}
        </div>
        <div className=" w-1/2 flex-row!">
          <button type="button" className="btn-conf w-64 rounded-full" onClick={handleUseConsultant}>
            {t('modal.partner.useConsultant')}
          </button>
          <button type="button" className="btn-cancel w-32 rounded-full" onClick={reset}>
            {t('modal.partner.reset')}
          </button>
        </div>
      </div>

      <div className="flex w-full mt-3 justify-center">
        <button
          type="submit"
          className="btn-save w-32"
          disabled={isLoading}
        >
          {isLoading ? t('modal.partner.saving') : t('modal.partner.save')}
        </button>

        {isEditing && (
          <button
            className="w-32 btn-cancel rounded-full"
            type="button"
            onClick={closeForm}
            disabled={isLoading}
          >
            {t('modal.partner.cancel')}
          </button>
        )}
      </div>
    </form>
  );
}

PartnerForm.defaultProps = {
  isEditing: false,
  partnerToEdit: undefined,
};
